import React, { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const faqData: FAQItem[] = [
    // General Questions
    {
      question: "What courses do you offer?",
      answer: "We offer comprehensive courses in ICAG (Institute of Chartered Accountants Ghana) and CITG (Chartered Institute of Taxation Ghana) programs. Our courses include study materials, video lectures, mock exams, and live online sessions.",
      category: "general"
    },
    {
      question: "How do I enroll in a course?",
      answer: "You can enroll by creating an account on our platform, browsing our course catalog, and selecting the course that fits your needs. Payment can be made online through our secure payment system.",
      category: "general"
    },
    {
      question: "What is the success rate of your students?",
      answer: "Our students have achieved a 95% success rate in professional examinations. We attribute this success to our comprehensive study materials, expert tutors, and structured learning approach.",
      category: "general"
    },
    {
      question: "Do you offer online learning?",
      answer: "Yes, we offer both online and blended learning options. Our platform includes video lectures, interactive study materials, and live online sessions with our expert tutors.",
      category: "general"
    },

    // Course Related
    {
      question: "How long do I have access to course materials?",
      answer: "Course access is granted until the relevant professional exam period for the current semester. This ensures you have sufficient time to prepare for your examinations.",
      category: "courses"
    },
    {
      question: "Can I share course materials with others?",
      answer: "No, course materials are for personal educational purposes only and must not be shared. All content is the intellectual property of Profs Training Solutions.",
      category: "courses"
    },
    {
      question: "What happens if I miss a live session?",
      answer: "Live sessions are recorded and made available for replay. However, we encourage regular attendance as it provides opportunities for real-time interaction with tutors and fellow students.",
      category: "courses"
    },
    {
      question: "Are there assignments and assessments?",
      answer: "Yes, regular assignments are mandatory for participation in live discussions. We also provide mock exams and practice questions to help you prepare for professional examinations.",
      category: "courses"
    },

    // Technical Support
    {
      question: "What if I have technical issues?",
      answer: "Our technical support team is available to help with any platform-related issues. You can contact us through our help center or email support for assistance.",
      category: "technical"
    },
    {
      question: "What devices are supported?",
      answer: "Our platform works on all modern devices including desktop computers, laptops, tablets, and smartphones. We recommend using the latest version of Chrome, Firefox, Safari, or Edge.",
      category: "technical"
    },
    {
      question: "Is there a mobile app?",
      answer: "Yes, we have a mobile app for both iOS and Android. You can download it from the App Store or Google Play Store.",
      category: "technical"
    },

    // Payment & Refunds
    {
      question: "What payment methods do you accept?",
      answer: "We accept various payment methods including mobile money, bank transfers, and online payments. All payments are processed securely through our payment partners.",
      category: "payment"
    },
    {
      question: "What is your refund policy?",
      answer: "No, we do not offer a refund policy for new enrollments.",
      category: "payment"
    },
    {
      question: "Are there installment payment options?",
      answer: "No, we do not offer installment payment options.",
      category: "payment"
    },

    // Legal & Privacy
    {
      question: "How do you protect my personal information?",
      answer: "Your enrollment information is used solely for processing registration and training purposes. We implement industry-standard security measures to protect your data and never share it with third parties.",
      category: "legal"
    },
    {
      question: "Can I cancel my enrollment?",
      answer: "No, you cannot cancel your enrollment.",
      category: "legal"
    },
    {
      question: "What are the terms of service?",
      answer: "Our terms of service outline the rules and guidelines for using our platform. This includes course access, student responsibilities, and platform usage policies. You can find the complete terms in our Legal Info section.",
      category: "legal"
    }
  ];

  const categories = [
    { id: 'all', name: 'All Questions', count: faqData.length },
    { id: 'general', name: 'General', count: faqData.filter(item => item.category === 'general').length },
    { id: 'courses', name: 'Courses', count: faqData.filter(item => item.category === 'courses').length },
    { id: 'technical', name: 'Technical', count: faqData.filter(item => item.category === 'technical').length },
    { id: 'payment', name: 'Payment', count: faqData.filter(item => item.category === 'payment').length },
    { id: 'legal', name: 'Legal', count: faqData.filter(item => item.category === 'legal').length }
  ];

  const filteredFAQs = activeCategory === 'all' 
    ? faqData 
    : faqData.filter(item => item.category === activeCategory);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(item => item !== index)
        : [...prev, index]
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
        <p className="text-lg text-gray-600">
          Find answers to common questions about our services, courses, and policies
        </p>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
              activeCategory === category.id
                ? 'bg-primary-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {category.name} ({category.count})
          </button>
        ))}
      </div>

      {/* FAQ Items */}
      <div className="space-y-4">
        {filteredFAQs.map((item, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden"
          >
            <button
              onClick={() => toggleItem(index)}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
            >
              <span className="font-semibold text-gray-900 pr-4">{item.question}</span>
              <ChevronDownIcon
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                  openItems.includes(index) ? 'rotate-180' : ''
                }`}
              />
            </button>
            {openItems.includes(index) && (
              <div className="px-6 pb-4 border-t border-gray-100">
                <p className="text-gray-700 leading-relaxed pt-4">{item.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Contact Support */}
      <div className="mt-12 text-center">
        <div className="bg-blue-50 rounded-xl p-8 border border-blue-200">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Still have questions?</h3>
          <p className="text-gray-600 mb-6">
            Can't find the answer you're looking for? Our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:profstudymate@email.com"
              className="inline-block bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors duration-200"
            >
              Contact Support
            </a>
            <a
              href="/legal"
              className="inline-block border-2 border-primary-600 text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-primary-50 transition-colors duration-200"
            >
              View Legal Info
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
